/**
 * Contact Page
 * ============
 * Contact form and information.
 */

import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal } from '../../components/animations/FadeReveal';
import { CONTACT_INFO } from '../../utils/constants';

/**
 * Contact Page Component
 * Contact form and company information.
 * 
 * @returns {JSX.Element} Contact page
 */
function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    institution: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
                <Mail className="w-4 h-4 text-dme-orange" />
                Get in Touch
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Contact Us
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
                Have questions? We'd love to hear from you. Send us a message 
                and we'll respond as soon as possible.
              </p>
            </div>
          </FadeReveal>
        </div>
      </section>
      
      {/* Contact Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <FadeReveal direction="left">
              <div>
                <h2 className="font-heading font-bold text-2xl lg:text-3xl text-dme-blue-800 mb-6">
                  Let's Start a Conversation
                </h2>
                <p className="text-dme-gray-600 mb-8">
                  Whether you're interested in a demo, have questions about our 
                  platform, or just want to say hello, we're here to help.
                </p>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-dme-blue-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-dme-blue" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-dme-blue-800 mb-1">Email</h4>
                      <a 
                        href={`mailto:${CONTACT_INFO.email}`}
                        className="text-dme-gray-600 hover:text-dme-orange transition-colors"
                      >
                        {CONTACT_INFO.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-dme-orange-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-dme-orange" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-dme-blue-800 mb-1">Phone</h4>
                      <a 
                        href={`tel:${CONTACT_INFO.phone}`}
                        className="text-dme-gray-600 hover:text-dme-orange transition-colors"
                      >
                        {CONTACT_INFO.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-dme-green-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-dme-green" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-dme-blue-800 mb-1">Address</h4>
                      <p className="text-dme-gray-600">
                        {CONTACT_INFO.address.street}<br />
                        {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Social Links */}
                <div>
                  <h4 className="font-semibold text-dme-blue-800 mb-4">Follow Us</h4>
                  <div className="flex gap-3">
                    <a 
                      href={CONTACT_INFO.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-dme-blue-50 flex items-center justify-center text-dme-blue hover:bg-dme-blue hover:text-white transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a 
                      href={CONTACT_INFO.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-dme-blue-50 flex items-center justify-center text-dme-blue hover:bg-dme-blue hover:text-white transition-colors"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a 
                      href={CONTACT_INFO.social.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-dme-blue-50 flex items-center justify-center text-dme-blue hover:bg-dme-blue hover:text-white transition-colors"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </FadeReveal>
            
            {/* Contact Form */}
            <FadeReveal direction="right">
              <div className="bg-dme-gray-50 rounded-2xl p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-dme-green flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-dme-blue-800 mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-dme-gray-600">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-dme-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-orange focus:border-transparent outline-none transition-all"
                        placeholder="John Smith"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-dme-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-orange focus:border-transparent outline-none transition-all"
                        placeholder="john@university.edu"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="institution" className="block text-sm font-medium text-dme-gray-700 mb-2">
                        Institution
                      </label>
                      <input
                        type="text"
                        id="institution"
                        name="institution"
                        value={formData.institution}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-orange focus:border-transparent outline-none transition-all"
                        placeholder="University Name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-dme-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-orange focus:border-transparent outline-none transition-all resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      isLoading={isSubmitting}
                      rightIcon={<Send className="w-5 h-5" />}
                    >
                      Send Message
                    </Button>
                  </form>
                )}
              </div>
            </FadeReveal>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
